import {
  canAddPinnedResources,
  canChangeAdminRights,
  canConvertGroupToDynamicMembershipRoster,
  canEditDynamicGroupMembershipRosterRules,
  canShowNetworkUsers,
  canStartThread,
  findById,
  getAddFavoriteFailureReason,
  getFulfilledAllCompanyGroupId,
  getFulfilledGroupPrivacyStatus,
  getGroupMembershipStatus,
  getStatusForId,
  isAdmin,
  isAllCompanyGroup,
  isDeleted,
  isDynamic,
  isDynamicRosterMembership,
  isFavorite,
  isInaccessible,
  isJoined,
  isPrivate,
  isThreadstarterRestricted,
  isViewerAutoEnrolled,
} from '@ms/yammer-data/dist/domains/viewerCommunityRelationships/selectors';
import type { Group } from '@ms/yammer-data/dist/state/types';

import { useIsPrivateContentModeActiveForViewer } from '../featureAccessSettings/selectors';
import { useIsMultiTenantOrganizationGroup } from '../groups/selectors';
import { useCanAccessMultiTenantOrganizationGroups } from '../multiTenantOrganization/selectors';
import { useSelector } from '../useSelector';

type BooleanSelector = (groupId: Group['id']) => boolean;

type UseViewerCommunityRelationship = (groupId: Group['id']) => ReturnType<typeof findById>;
/**
 * @deprecated PERFORMANCE This returns an entity object, which will be a new object instance and will force re-renders
 * whenever the entity changes. This should be replaced by a `useViewerCommunityRelationshipField` selector, or consumers
 * should call the more specific selectors in this file.
 */
export const useViewerCommunityRelationship: UseViewerCommunityRelationship = (groupId) =>
  useSelector((state) => findById(state, groupId));

type UseStatus = (groupId: Group['id']) => ReturnType<typeof getStatusForId>;
export const useStatus: UseStatus = (groupId) => useSelector((state) => getStatusForId(state, groupId));

export const useIsAdmin: BooleanSelector = (groupId) => useSelector((state) => isAdmin(state, groupId));
export const useIsAllCompanyGroup: BooleanSelector = (groupId) =>
  useSelector((state) => isAllCompanyGroup(state, groupId));
export const useIsThreadstarterRestricted: BooleanSelector = (groupId) =>
  useSelector((state) => isThreadstarterRestricted(state, groupId));
export const useCanAddPinnedResources: BooleanSelector = (groupId) =>
  useSelector((state) => canAddPinnedResources(state, groupId));
export const useIsDeleted: BooleanSelector = (groupId) => useSelector((state) => isDeleted(state, groupId));
export const useIsFavorite: BooleanSelector = (groupId) => useSelector((state) => isFavorite(state, groupId));
export const useIsInaccessible: BooleanSelector = (groupId) => {
  const isInaccessibleGroup = useSelector((state) => isInaccessible(state, groupId));
  const isUnlicensed = useIsUnlicensed(groupId);

  return isInaccessibleGroup || isUnlicensed;
};

export const useIsJoined: BooleanSelector = (groupId) => useSelector((state) => isJoined(state, groupId));
export const useIsPrivate: BooleanSelector = (groupId) => useSelector((state) => isPrivate(state, groupId));
export const useCanStartThread: BooleanSelector = (groupId) => useSelector((state) => canStartThread(state, groupId));
export const useIsDynamic: BooleanSelector = (groupId) => useSelector((state) => isDynamic(state, groupId));
export const useIsDynamicRosterMembership: BooleanSelector = (groupId) =>
  useSelector((state) => isDynamicRosterMembership(state, groupId));
export const useIsViewerAutoEnrolled: BooleanSelector = (groupId) =>
  useSelector((state) => isViewerAutoEnrolled(state, groupId));
export const useCanConvertGroupToDynamicMembershipRoster: BooleanSelector = (groupId) =>
  useSelector((state) => canConvertGroupToDynamicMembershipRoster(state, groupId));

export const useCanEditDynamicGroupMembershipRosterRules: BooleanSelector = (groupId) =>
  useSelector((state) => canEditDynamicGroupMembershipRosterRules(state, groupId));

type UseMembershipStatus = (groupId: Group['id']) => ReturnType<typeof getGroupMembershipStatus>;
export const useMembershipStatus: UseMembershipStatus = (groupId) =>
  useSelector((state) => getGroupMembershipStatus(state, groupId));

type UseAddFavoriteFailureReason = () => ReturnType<typeof getAddFavoriteFailureReason>;
export const useAddFavoriteFailureReason: UseAddFavoriteFailureReason = () => useSelector(getAddFavoriteFailureReason);

type UseFulfilledGroupPrivacyStatus = (groupId?: Group['id']) => ReturnType<typeof getFulfilledGroupPrivacyStatus>;
export const useFulfilledGroupPrivacyStatus: UseFulfilledGroupPrivacyStatus = (groupId) =>
  useSelector((state) => getFulfilledGroupPrivacyStatus(state, groupId));

type UseFulfilledAllCompanyGroupId = () => ReturnType<typeof getFulfilledAllCompanyGroupId>;
export const useFulfilledAllCompanyGroupId: UseFulfilledAllCompanyGroupId = () =>
  useSelector((state) => getFulfilledAllCompanyGroupId(state));

export const useCanChangeAdminRights: BooleanSelector = (groupId) =>
  useSelector((state) => canChangeAdminRights(state, groupId));

export const useCanShowNetworkUsers: BooleanSelector = (groupId) =>
  useSelector((state) => canShowNetworkUsers(state, groupId));

type useIsUnlicensedGroup = (groupId: Group['id']) => boolean;
export const useIsUnlicensed: useIsUnlicensedGroup = (groupId) => {
  const isMultiTenantOrganizationGroup = useIsMultiTenantOrganizationGroup(groupId);
  const canAccessMultiTenantOrganizationGroups = useCanAccessMultiTenantOrganizationGroups();

  return isMultiTenantOrganizationGroup && !canAccessMultiTenantOrganizationGroups;
};

type UseIsViewerAccessingNonMemberPrivateGroupThroughPrivateContentMode = (groupId: Group['id']) => boolean;
export const useIsViewerAccessingNonMemberPrivateGroupThroughPrivateContentMode: UseIsViewerAccessingNonMemberPrivateGroupThroughPrivateContentMode =
  (groupId) => {
    const isPrivateGroup = useIsPrivate(groupId);
    const isMember = useIsJoined(groupId);
    const isPrivateContentModeActiveForViewer = useIsPrivateContentModeActiveForViewer();

    return isPrivateGroup && !isMember && isPrivateContentModeActiveForViewer;
  };
